import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col } from "reactstrap";
import { Button } from "zina-dogi-react";
import Select from "react-select";
import AlertTypeBadge, { ALERT_TYPES, alertTypeOptions, formatAlertTypeOption } from "~/utils/components/alert_type_badge";
import "./notification_alert_modal.scss";

const EMPTY_FORM = {
    type: null,
    title: "",
    message: "",
    start_date: "",
    end_date: ""
};

const buildFormFromNotification = notification => ({
    type: alertTypeOptions.find(
        o => o.value === (notification.type || notification.level || "").toLowerCase()
    ) || null,
    title: notification.title || "",
    message: notification.message || "",
    start_date: notification.start_date ? notification.start_date.split("T")[0] : "",
    end_date: notification.end_date ? notification.end_date.split("T")[0] : ""
});

const NotificationAlertModal = ({ isOpen, notification, onClose, onSave, hideDates = false, saving = false, saveError = null }) => {
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isOpen) {
            setForm(notification ? buildFormFromNotification(notification) : { ...EMPTY_FORM });
            setErrors({});
        }
    }, [isOpen, notification]);

    const handleChange = (key, value) => {
        setForm(prev => ({ ...prev, [key]: value }));
        if (errors[key]) setErrors(prev => ({ ...prev, [key]: null }));
    };

    const validate = () => {
        const next = {};
        if (!form.type)            next.type    = "Type is required";
        if (!form.title.trim())   next.title   = "Title is required";
        if (!form.message.trim()) next.message = "Message is required";
        return next;
    };

    const handleSave = () => {
        const next = validate();
        if (Object.keys(next).length > 0) { setErrors(next); return; }
        const body = {
            type: form.type.value,
            title: form.title,
            message: form.message,
            ...(!hideDates && { start_date: form.start_date || null }),
            ...(!hideDates && { end_date:   form.end_date   || null })
        };
        onSave(notification ? notification.id : null, body)
            .then(() => { onClose(); });
    };

    const isEditMode = Boolean(notification && notification.id);

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="lg" className="notification-alert-modal">
            <ModalHeader toggle={onClose}>
                {isEditMode ? "Edit Alert Notification" : "New Alert Notification"}
            </ModalHeader>
            <ModalBody>
                <Row>
                    <Col sm="12" md="6">
                        <div className="form-group">
                            <label>Type <span className="text-danger">*</span></label>
                            <Select
                                value={form.type}
                                options={alertTypeOptions}
                                isClearable
                                formatOptionLabel={formatAlertTypeOption}
                                onChange={val => handleChange("type", val)}
                                styles={errors.type ? { control: base => ({ ...base, borderColor: "#dc3545" }) } : {}}
                            />
                            {errors.type && <div className="invalid-feedback d-block">{errors.type}</div>}
                        </div>
                    </Col>
                    <Col sm="12" md="6">
                        <div className="form-group">
                            <label>Title <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className={`form-control${errors.title ? " is-invalid" : ""}`}
                                placeholder="Type here..."
                                value={form.title}
                                onChange={e => handleChange("title", e.target.value)}
                            />
                            {errors.title && <div className="invalid-feedback">{errors.title}</div>}
                        </div>
                    </Col>
                </Row>
                <div className="form-group">
                    <label>Message <span className="text-danger">*</span></label>
                    <textarea
                        className={`form-control${errors.message ? " is-invalid" : ""}`}
                        placeholder="Type here..."
                        rows={4}
                        value={form.message}
                        onChange={e => handleChange("message", e.target.value)}
                    />
                    {errors.message && <div className="invalid-feedback">{errors.message}</div>}
                </div>
                {!hideDates && (
                <Row>
                    <Col sm="12" md="6">
                        <div className="form-group">
                            <label>Start Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={form.start_date}
                                onChange={e => handleChange("start_date", e.target.value)}
                            />
                        </div>
                    </Col>
                    <Col sm="12" md="6">
                        <div className="form-group">
                            <label>End Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={form.end_date}
                                onChange={e => handleChange("end_date", e.target.value)}
                            />
                        </div>
                    </Col>
                </Row>
                )}
                <hr />
                <div className="notification-alert-modal__preview">
                    <small className="text-muted notification-alert-modal__preview-label">Preview</small>
                    <div className={`notification-alert-modal__preview-box alert-preview--${form.type ? form.type.value : "info"}`}>
                        <div className="notification-alert-modal__preview-header">
                            <strong>{form.title || <span className="text-muted">Alert title…</span>}</strong>
                        </div>
                        <div className="notification-alert-modal__preview-body">
                            <p style={{ whiteSpace: "pre-line", marginBottom: form.start_date || form.end_date ? "0.5rem" : 0 }}>
                                {form.message || <span className="text-muted">Alert message will appear here…</span>}
                            </p>
                            {!hideDates && (form.start_date || form.end_date) && (
                                <small className="text-muted">
                                    {form.start_date && `From: ${form.start_date}`}
                                    {form.start_date && form.end_date && " — "}
                                    {form.end_date && `Until: ${form.end_date}`}
                                </small>
                            )}
                        </div>
                    </div>
                </div>
            </ModalBody>
            {saveError && (
                <div className="alert alert-danger mx-3 mb-0" role="alert">
                    Failed to save. Please try again.
                </div>
            )}
            <ModalFooter>
                <Button color="primary" onClick={handleSave} disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                </Button>
                <Button color="danger" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default NotificationAlertModal;
